import { Eye } from "@untitledui/icons";
import { type ReactNode, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { reverseGeocode } from "../../services/geocoding-service";
import type { EventListItem } from "../../types/events";
import { buildEventTechnicalDisplayRows } from "../build-event-detail-rows";
import {
	FUEL_TYPE_LABELS_PT,
	TRACKING_EVENT_OPERATIONAL_COPY,
	TRACKING_EVENT_TRIPS_ROW_COPY,
} from "../events.config";
import { pickAttributeText } from "../pick-event-attribute";
import { EventFieldHelp } from "./event-field-help";

type EventAttributes = Record<string, number | string> | null | undefined;

function formatFuelType(code: number | null): string {
	if (code === null) {
		return "";
	}
	return FUEL_TYPE_LABELS_PT[code] ?? "";
}

function DetailField({
	label,
	tooltip,
	children,
}: {
	label: string;
	tooltip?: string;
	children: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1">
			<dt className="flex items-center gap-1 font-medium text-tertiary text-xs">
				<span>{label}</span>
				{tooltip ? <EventFieldHelp text={tooltip} /> : null}
			</dt>
			<dd className="text-primary text-sm">{children}</dd>
		</div>
	);
}

function renderOperationalBlock(
	workSchedule: string,
	distanceKm: string,
	fuelLiters: string,
	fuelTypeLabel: string
): ReactNode {
	const hasAny = [workSchedule, distanceKm, fuelLiters].some(
		(value) => value.trim() !== ""
	);
	if (!hasAny) {
		return null;
	}

	const op = TRACKING_EVENT_OPERATIONAL_COPY;

	return (
		<div className="rounded-xl border border-border-secondary bg-primary p-4 shadow-xs md:p-5">
			<dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{workSchedule ? (
					<DetailField
						label={op.workSchedule.label}
						tooltip={op.workSchedule.description}
					>
						{workSchedule}
					</DetailField>
				) : null}
				{distanceKm ? (
					<DetailField
						label={op.distanceKm.label}
						tooltip={op.distanceKm.description}
					>
						{`${distanceKm} Km`}
					</DetailField>
				) : null}
				{fuelLiters ? (
					<DetailField
						label={op.fuelLiters.label}
						tooltip={op.fuelLiters.description}
					>
						{`${fuelLiters} l`}
					</DetailField>
				) : null}
				{fuelLiters ? (
					<DetailField
						label={op.fuelType.label}
						tooltip={op.fuelType.description}
					>
						{fuelTypeLabel}
					</DetailField>
				) : null}
			</dl>
		</div>
	);
}

type TechnicalAttrRow = {
	label: string;
	value: ReactNode;
	tooltip?: string;
	action?: ReactNode;
};

function renderTechnicalBlock(rows: TechnicalAttrRow[]): ReactNode {
	if (rows.length === 0) {
		return null;
	}

	return (
		<div className="mt-4">
			<dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
				{rows.map((row, rowIndex) => (
					<div
						className="flex flex-col gap-0.5"
						key={`${row.label}-${rowIndex}`}
					>
						<dt className="flex items-center gap-1 text-tertiary text-xs">
							<span>{row.label}</span>
							{row.tooltip ? <EventFieldHelp text={row.tooltip} /> : null}
						</dt>
						<dd className="flex items-center gap-2 font-medium text-primary text-sm">
							<span className="whitespace-pre-line">{row.value}</span>
							{row.action ?? null}
						</dd>
					</div>
				))}
			</dl>
		</div>
	);
}

function trimCoord(value: string | null | undefined): string {
	if (value === null || value === undefined) {
		return "";
	}
	return String(value).trim();
}

function hasCoordPair(
	latitude?: string | null,
	longitude?: string | null
): boolean {
	return Boolean(trimCoord(latitude) && trimCoord(longitude));
}

function EventAddressFieldValue({ item }: { item: EventListItem }) {
	const [resolvedAddressText, setResolvedAddressText] = useState<string>(
		"Clique para exibir localização"
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasLoadedAddress, setHasLoadedAddress] = useState(false);

	const startCoordsAvailable = hasCoordPair(
		item.startLatitude,
		item.startLongitude
	);
	const endCoordsAvailable = hasCoordPair(item.endLatitude, item.endLongitude);
	const latitude = startCoordsAvailable
		? trimCoord(item.startLatitude)
		: endCoordsAvailable
			? trimCoord(item.endLatitude)
			: "";
	const longitude = startCoordsAvailable
		? trimCoord(item.startLongitude)
		: endCoordsAvailable
			? trimCoord(item.endLongitude)
			: "";

	const canResolve = Boolean(latitude && longitude);

	const handleResolveAddress = useCallback(async () => {
		if (!(canResolve && !isLoading) || hasLoadedAddress) {
			return;
		}
		setIsLoading(true);
		try {
			const resolved = await reverseGeocode(latitude, longitude);
			setResolvedAddressText(resolved || "Endereço não encontrado");
			setHasLoadedAddress(true);
		} finally {
			setIsLoading(false);
		}
	}, [canResolve, hasLoadedAddress, isLoading, latitude, longitude]);

	return (
		<div className="flex items-center gap-2">
			<span>{isLoading ? "Carregando..." : resolvedAddressText}</span>
			{canResolve ? (
				<button
					aria-label="Buscar localização por coordenadas"
					className="text-[#175CD3] hover:text-[#1849A9] disabled:cursor-not-allowed disabled:text-gray-400"
					disabled={isLoading || hasLoadedAddress}
					onClick={handleResolveAddress}
					type="button"
				>
					<Eye className="size-4" />
				</button>
			) : null}
		</div>
	);
}

export function renderEventsDetailRow(item: EventListItem): ReactNode {
	const attrs = item.attributes;

	const workSchedule = pickAttributeText(attrs, [
		"vehicleWorkSchedule",
		"workSchedule",
		"VehicleWorkSchedule",
	]);

	const distanceKmFromAttributes = pickAttributeText(attrs, [
		"distanceKm",
		"DistanceKm",
		"distance_km",
	]);
	const fuelLitersFromAttributes = pickAttributeText(attrs, [
		"fuelLiters",
		"FuelLiters",
		"fuel_liters",
	]);
	const distanceKm =
		distanceKmFromAttributes ||
		(item.tripDistance != null ? String(item.tripDistance) : "");
	const fuelLiters =
		fuelLitersFromAttributes ||
		(item.tripFuelConsumption != null ? String(item.tripFuelConsumption) : "");

	const fuelTypeLabel = formatFuelType(item.fuelType);

	const attrRows = buildEventTechnicalDisplayRows(item.eventType, attrs ?? {});
	const totalTrips = item.tripIds?.length ?? 0;

	const operational = renderOperationalBlock(
		item.eventType === 10 ? "" : workSchedule,
		item.eventType === 10 ? "" : distanceKm,
		item.eventType === 10 ? "" : fuelLiters,
		fuelTypeLabel
	);

	const tripsCopy = TRACKING_EVENT_TRIPS_ROW_COPY;
	const tripIdsCsv = (item.tripIds ?? []).join(",");

	return (
		<div className="rounded-xl border border-border-secondary bg-alpha-white p-6 shadow-xs md:px-6">
			{operational}

			{renderTechnicalBlock([
				...(item.eventType !== 13
					? [
							{
								label: "Endereço",
								value: <EventAddressFieldValue item={item} />,
							},
						]
					: []),
				...(item.eventType === 10 && Boolean(item.vehicle.id) && totalTrips > 0
					? [
							{
								label: tripsCopy.label,
								value: `${totalTrips} ${totalTrips === 1 ? "viagem" : "viagens"}`,
								tooltip: tripsCopy.description,
								action: (
									<Link
										className="font-medium underline"
										style={{ color: "#175CD3" }}
										to={`/tracking?vehicleId=${item.vehicle.id}&tripIds=${tripIdsCsv}`}
									>
										Ver viagens
									</Link>
								),
							},
						]
					: item.eventType === 10
						? [
								{
									label: tripsCopy.label,
									value:
										"As viagens serão exibidas no início do próximo expediente.",
									tooltip: tripsCopy.description,
								},
							]
						: []),
				...(item.eventType === 10 && distanceKm.trim() !== ""
					? [
							{
								label: TRACKING_EVENT_OPERATIONAL_COPY.distanceKm.label,
								value: `${distanceKm} Km`,
								tooltip: TRACKING_EVENT_OPERATIONAL_COPY.distanceKm.description,
							},
						]
					: []),
				...(item.eventType === 10 && fuelLiters.trim() !== ""
					? [
							{
								label: TRACKING_EVENT_OPERATIONAL_COPY.fuelLiters.label,
								value: `${fuelLiters} l`,
								tooltip: TRACKING_EVENT_OPERATIONAL_COPY.fuelLiters.description,
							},
						]
					: []),
				...(item.eventType === 10 && fuelTypeLabel.trim() !== ""
					? [
							{
								label: TRACKING_EVENT_OPERATIONAL_COPY.fuelType.label,
								value: fuelTypeLabel,
								tooltip: TRACKING_EVENT_OPERATIONAL_COPY.fuelType.description,
							},
						]
					: []),
				...attrRows,
			])}
		</div>
	);
}
